import { AlertTriangle, Clock, RefreshCw, Terminal, XCircle } from "lucide-react";
import { useCallback, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { SeverityBadge } from "@/components/shared/SeverityBadge";
import { StatCard } from "@/components/shared/StatCard";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { usePolling } from "@/hooks/usePolling";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { PageShell, PageHeader } from "@/components/layout/PageShell";
import { formatDuration, generateColorScale } from "@/lib/chart-helpers";
import { incompleteNotice, probe, probeOr, unreachableNames } from "@/lib/probe";
import type { AlertSeverity } from "@/types/metrics";
import type { TaskRecord } from "@/types/platform";

type AgentError = {
  ts: number;
  runId: string;
  model: string;
  provider: string;
  error?: string;
  durationMs: number;
  sessionKey: string;
};

type CronRun = {
  ts: number;
  jobId: string;
  status: string;
  error?: string;
  durationMs?: number;
  model?: string;
};

type BackendAlert = {
  id: string;
  ruleId: string;
  severity: string;
  message: string;
  value: number;
  threshold: number;
  triggeredAt: number;
  resolvedAt?: number;
};

type LatencyStat = {
  method: string;
  count: number;
  errorRate: number;
};

type ErrorData = {
  agentErrors: AgentError[];
  cronErrors: CronRun[];
  alerts: BackendAlert[];
  latencyErrors: LatencyStat[];
  failedTasks: TaskRecord[];
  logErrors: string[];
  /** Labels of the probes that FAILED this cycle. Non-empty means every
   *  count on this page is a floor, not a reading — surfaced as a banner
   *  so a failed fan-out can never render as "0 errors = healthy". */
  unreachable: string[];
};

const PIE_COLORS = generateColorScale(6);

export function ErrorDashboard() {
  const { rpc, state } = useGatewayRpc();
  const api = usePlatformApi();
  const [timeframe, setTimeframe] = useState(24);

  const fetcher = useCallback(async (): Promise<ErrorData> => {
    const since = Date.now() - timeframe * 3_600_000;

    // Each probe is SETTLED, never swallowed: a failure must not read as
    // "zero errors" on the page whose job is surfacing errors. See
    // lib/probe.ts for why this is not a blanket rethrow.
    const [agentsRes, cronRes, alertsRes, latencyRes, tasksRes, logsRes] = await Promise.all([
      probe(rpc<{ events: AgentError[] }>("metrics.agents", { since })),
      probe(rpc<{ entries: CronRun[] }>("cron.runs", { scope: "all", status: "error", limit: 50 })),
      probe(rpc<{ alerts: BackendAlert[] }>("metrics.alerts")),
      probe(rpc<{ stats: LatencyStat[] }>("metrics.latency", { since })),
      probe(api.listTasks({ status: "failed", limit: 20 }).then((r) => r.data)),
      probe(rpc<{ lines: string[] }>("logs.tail", { lines: 200 })),
    ]);

    const unreachable = unreachableNames({
      "agent metrics": agentsRes,
      "cron runs": cronRes,
      alerts: alertsRes,
      latency: latencyRes,
      "failed tasks": tasksRes,
      logs: logsRes,
    });

    const agentErrors = (probeOr(agentsRes, { events: [] }).events ?? []).filter((e) => e.error);
    const cronErrors = (probeOr(cronRes, { entries: [] }).entries ?? []).filter((e) => e.status === "error");
    const latencyErrors = (probeOr(latencyRes, { stats: [] }).stats ?? []).filter((s) => s.errorRate > 0);
    const logErrors = (probeOr(logsRes, { lines: [] }).lines ?? []).filter((l) =>
      l.includes("[error]") || l.includes("[ERROR]") || l.includes("FATAL") || l.includes("Uncaught"),
    );

    return {
      agentErrors,
      cronErrors,
      alerts: probeOr(alertsRes, { alerts: [] }).alerts ?? [],
      latencyErrors,
      failedTasks: probeOr(tasksRes, [] as TaskRecord[]),
      logErrors,
      unreachable,
    };
  }, [rpc, api, timeframe]);

  const { data, loading } = usePolling(fetcher, 15_000, { enabled: state === "connected" });

  const totalErrors =
    (data?.agentErrors.length ?? 0) +
    (data?.cronErrors.length ?? 0) +
    (data?.failedTasks.length ?? 0) +
    (data?.logErrors.length ?? 0);

  const activeAlerts = (data?.alerts ?? []).filter((a) => !a.resolvedAt);

  // `totalErrors` is a FLOOR when any probe failed — the page must say so
  // rather than presenting a partial count as the whole truth.
  const anyUnreachable = (data?.unreachable.length ?? 0) > 0;
  const incompleteBanner = incompleteNotice(data?.unreachable ?? [], 6);

  /** Did THIS panel's own probe fail? The page-level banner is not enough:
   *  each panel below states a definite negative ("No agent errors") that is
   *  a claim about the RUNTIME, and we may only make it when we actually
   *  read that source. */
  const down = (name: string) => data?.unreachable.includes(name) ?? false;
  const UNREADABLE = "Could not read this source — see the notice above.";

  // Error category breakdown for pie chart
  const categories = [
    { name: "Agent Failures", value: data?.agentErrors.length ?? 0 },
    { name: "Cron Errors", value: data?.cronErrors.length ?? 0 },
    { name: "Task Failures", value: data?.failedTasks.length ?? 0 },
    { name: "Log Errors", value: data?.logErrors.length ?? 0 },
  ].filter((c) => c.value > 0);

  // RPC methods with highest error rates
  const topErrorMethods = (data?.latencyErrors ?? [])
    .sort((a, b) => b.errorRate - a.errorRate)
    .slice(0, 10);

  if (state !== "connected" && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Error Dashboard</h1>
        <p className="mt-2 text-sm text-muted">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Error Dashboard"
        actions={
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(Number(e.target.value))}
            className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm"
          >
            <option value={1}>Last 1 hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={72}>Last 3 days</option>
          </select>
        }
      />

      {/* Incomplete-reading banner. MUST render above the counts: a failed
          fan-out otherwise shows "0 errors" in healthy green, which reads
          as an all-clear when the truth is that we could not look. */}
      {incompleteBanner && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
          data-testid="error-dashboard-incomplete"
          role="status"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{incompleteBanner}</span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          title="Total Errors"
          value={anyUnreachable ? `${totalErrors}+` : String(totalErrors)}
          // Never paint a green "all clear" off an incomplete reading.
          sparkColor={
            totalErrors > 0
              ? "var(--color-critical)"
              : anyUnreachable
                ? "var(--color-warning)"
                : "var(--color-healthy)"
          }
        />
        {/* A per-source card must show "—" when ITS probe failed; a bare 0
            here is the same lie the page-level banner exists to prevent. */}
        <StatCard title="Agent Failures" value={down("agent metrics") ? "—" : String(data?.agentErrors.length ?? 0)} />
        <StatCard title="Task Failures" value={down("failed tasks") ? "—" : String(data?.failedTasks.length ?? 0)} />
        <StatCard title="Active Alerts" value={down("alerts") ? "—" : String(activeAlerts.length)} />
      </div>

      {/* Error Breakdown + Alerts */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Category Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Error Categories</CardTitle>
          </CardHeader>
          <CardContent>
            {categories.length === 0 ? (
              <p className="py-8 text-center text-sm text-faint">
                {["agent metrics", "cron runs", "failed tasks", "logs"].some(down)
                  ? UNREADABLE
                  : "No errors in this timeframe"}
              </p>
            ) : (
              <div className="flex items-start gap-4">
                <ResponsiveContainer width="50%" height={140}>
                  <PieChart>
                    <Pie data={categories} dataKey="value" cx="50%" cy="50%" outerRadius={55} innerRadius={25}>
                      {categories.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => [v, "errors"]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5 text-xs">
                  {categories.map((c, i) => (
                    <div key={c.name} className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="flex-1">{c.name}</span>
                      <span className="font-semibold tabular-nums">{c.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Alerts */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <AlertTriangle className="h-4 w-4" /> Active Alerts ({down("alerts") ? "—" : activeAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeAlerts.length === 0 ? (
              <p className="py-8 text-center text-sm text-faint">
                {down("alerts") ? UNREADABLE : "No active alerts"}
              </p>
            ) : (
              <div className="space-y-2">
                {activeAlerts.map((a) => (
                  <div key={a.id} className="flex items-start gap-2 rounded-md border p-2 text-sm dark:border-border">
                    <SeverityBadge severity={a.severity as AlertSeverity} />
                    <div>
                      <p className="font-medium">{a.message}</p>
                      <p className="text-xs text-muted-foreground">{new Date(a.triggeredAt).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Agent Errors Feed */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <XCircle className="h-4 w-4" /> Agent Errors ({down("agent metrics") ? "—" : (data?.agentErrors.length ?? 0)})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data?.agentErrors.length ?? 0) === 0 ? (
            <p className="py-6 text-center text-sm text-faint">
              {down("agent metrics") ? UNREADABLE : "No agent errors"}
            </p>
          ) : (
            <div className="max-h-[300px] space-y-2 overflow-y-auto">
              {data!.agentErrors.sort((a, b) => b.ts - a.ts).slice(0, 30).map((e) => (
                <div key={`${e.runId}-${e.ts}`} className="flex items-start gap-2 rounded-md border p-2 text-sm dark:border-border">
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px]">{e.model.split("-").slice(0, 3).join("-")}</Badge>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatDuration(e.durationMs)}
                      </span>
                      <span className="text-xs text-muted-foreground">{new Date(e.ts).toLocaleTimeString()}</span>
                    </div>
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">{e.error}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Failed Tasks + RPC Error Rates */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Failed Tasks */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <RefreshCw className="h-4 w-4" /> Failed Tasks ({down("failed tasks") ? "—" : (data?.failedTasks.length ?? 0)})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.failedTasks.length ?? 0) === 0 ? (
              <p className="py-6 text-center text-sm text-faint">
                {down("failed tasks") ? UNREADABLE : "No failed tasks"}
              </p>
            ) : (
              <div className="max-h-[250px] space-y-2 overflow-y-auto">
                {data!.failedTasks.map((t) => (
                  <div key={t.taskId} className="rounded-md border p-2 text-sm dark:border-border">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-3.5 w-3.5 text-red-500" />
                      <span className="font-medium">{t.type}</span>
                      <span className="text-xs text-muted-foreground">{new Date(t.createdAt).toLocaleString()}</span>
                    </div>
                    {t.error && <p className="mt-1 text-xs text-red-400">{typeof t.error === "string" ? t.error : JSON.stringify(t.error)}</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* RPC Error Rates */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Terminal className="h-4 w-4" /> RPC Error Rates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topErrorMethods.length === 0 ? (
              <p className="py-6 text-center text-sm text-faint">
                {down("latency") ? UNREADABLE : "No RPC errors"}
              </p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-wider text-muted dark:border-border">
                    <th className="pb-2 pr-4">Method</th>
                    <th className="pb-2 pr-4 text-right">Calls</th>
                    <th className="pb-2 text-right">Error Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {topErrorMethods.map((m) => (
                    <tr key={m.method} className="border-b border-border dark:border-border">
                      <td className="py-1.5 pr-4 font-mono text-xs">{m.method}</td>
                      <td className="py-1.5 pr-4 text-right tabular-nums">{m.count}</td>
                      <td className={`py-1.5 text-right tabular-nums ${m.errorRate > 0.1 ? "text-red-500" : "text-muted-foreground"}`}>
                        {(m.errorRate * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Log Errors */}
      {(data?.logErrors.length ?? 0) > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Recent Log Errors ({data?.logErrors.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[200px] space-y-1 overflow-y-auto font-mono text-xs">
              {data!.logErrors.slice(-20).map((line, i) => (
                <div key={i} className="rounded bg-red-50 p-1.5 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  {line.slice(0, 200)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data && <p className="text-center text-sm text-faint">Loading error data...</p>}
    </PageShell>
  );
}
